import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Library, Upload, Mic, Trash2, Pencil, Check, Loader2, Send, X } from "lucide-react";
import api, { getErrorMsg } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { AudioRecorder } from "./AudioRecorder";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const dauer = (s) => (s == null ? null : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")} min`);

function Status({ f }) {
  const map = {
    processing: ["bg-amber-100 text-amber-700", "wird optimiert…"],
    ready: ["bg-emerald-100 text-emerald-700", "bereit"],
    ready_unprocessed: ["bg-gray-100 text-gray-600", "bereit (ungeprüft)"],
    failed: ["bg-red-100 text-red-700", "Fehler"],
  };
  const [cls, txt] = map[f.status] || map.failed;
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${cls}`}>{txt}</span>;
}

function Eintrag({ f, orders, onChange }) {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(f.name);
  const [ziel, setZiel] = useState("");
  const [busy, setBusy] = useState(false);
  const ready = f.status === "ready" || f.status === "ready_unprocessed";
  const token = localStorage.getItem("token");

  const umbenennen = async () => {
    try { await api.patch(`/mbox/library/${f.id}`, { name }); setEdit(false); onChange(); }
    catch (e) { toast.error(getErrorMsg(e, "Umbenennen fehlgeschlagen")); }
  };
  const loeschen = async () => {
    if (!window.confirm(`„${f.name}“ aus der Durchsagen-Datenbank löschen?\n\nBereits mit Veranstaltungen verknüpfte Durchsagen bleiben dort erhalten.`)) return;
    try { await api.delete(`/mbox/library/${f.id}`); toast.success("Gelöscht"); onChange(); }
    catch (e) { toast.error(getErrorMsg(e, "Löschen fehlgeschlagen")); }
  };
  const uebernehmen = async () => {
    if (!ziel) return;
    setBusy(true);
    try {
      await api.post(`/mbox/library/${f.id}/to-order/${ziel}`);
      const o = orders.find(x => String(x.order_pk) === String(ziel));
      toast.success(`„${f.name}“ mit „${o?.event || `Auftrag ${ziel}`}“ verknüpft – wird auf die Masten übertragen`);
      setZiel("");
      onChange(true);
    } catch (e) { toast.error(getErrorMsg(e, "Verknüpfen fehlgeschlagen")); }
    finally { setBusy(false); }
  };

  return (
    <li className="py-3 border-b border-gray-100 last:border-0" data-testid={`library-file-${f.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {edit ? (
          <>
            <Input value={name} onChange={e => setName(e.target.value)} className="h-10 sm:h-8 max-w-xs text-base sm:text-sm" data-testid={`library-name-input-${f.id}`} />
            <Button size="sm" variant="outline" onClick={umbenennen} data-testid={`library-name-save-${f.id}`}><Check className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" onClick={() => { setEdit(false); setName(f.name); }}><X className="w-4 h-4" /></Button>
          </>
        ) : (
          <>
            <span className="text-sm font-semibold text-gray-900" data-testid={`library-name-${f.id}`}>{f.name}</span>
            <button type="button" onClick={() => setEdit(true)} className="text-gray-400 hover:text-fuchsia-600 p-2 -m-2 sm:p-0 sm:m-0" title="Umbenennen" data-testid={`library-rename-${f.id}`}><Pencil className="w-3.5 h-3.5" /></button>
          </>
        )}
        <Status f={f} />
        {dauer(f.duration_sec) && <span className="text-[11px] text-gray-400">{dauer(f.duration_sec)}</span>}
        <span className="text-[11px] text-gray-400 sm:ml-auto w-full sm:w-auto">{f.uploaded_by} · {new Date(f.uploaded_at).toLocaleString("de-DE")}</span>
      </div>
      {f.status === "failed" && <p className="text-xs text-red-600 mt-1">{f.error}</p>}
      <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2">
        {ready && <audio controls preload="none" className="h-9 w-full sm:max-w-sm" src={`${API_BASE}/api/mbox/library/${f.id}/stream?token=${token}`} data-testid={`library-preview-${f.id}`} />}
        {ready && orders.length > 0 && (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <select value={ziel} onChange={e => setZiel(e.target.value)} className="h-10 sm:h-9 flex-1 min-w-0 px-2 border border-gray-200 rounded-md text-base sm:text-sm bg-white" data-testid={`library-target-${f.id}`}>
              <option value="">Mit Veranstaltung verknüpfen…</option>
              {orders.map(o => <option key={o.order_pk} value={o.order_pk}>{o.event || `Auftrag ${o.order_pk}`}{o.active ? " (läuft)" : ""}</option>)}
            </select>
            <Button size="sm" onClick={uebernehmen} disabled={!ziel || busy} className="bg-fuchsia-600 hover:bg-fuchsia-700 h-10 sm:h-9 px-3" data-testid={`library-to-order-${f.id}`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        )}
        <Button size="sm" variant="ghost" onClick={loeschen} className="text-gray-400 hover:text-red-600 h-10 sm:h-9 self-end sm:self-auto" data-testid={`library-delete-${f.id}`}>
          <Trash2 className="w-4 h-4" /><span className="ml-1 sm:hidden">Löschen</span>
        </Button>
      </div>
    </li>
  );
}

// Durchsagen-Datenbank des Kunden: gehoert der Kundennummer (alle Logins dieser Kundennummer teilen sie)
export function CustomerAudioLibrary({ onOrdersChanged }) {
  const [data, setData] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [modus, setModus] = useState(null); // null | "upload" | "record"
  const [uploadName, setUploadName] = useState("");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const dragDepth = useRef(0);
  const fileRef = useRef(null);

  const load = useCallback(() => api.get("/mbox/library")
    .then(r => { setData(r.data); setFehler(null); })
    .catch(e => setFehler(getErrorMsg(e, "Durchsagen-Datenbank nicht ladbar"))), []);
  useEffect(() => { load(); }, [load]);
  // waehrend der Verarbeitung regelmaessig nachladen
  const processing = (data?.files || []).some(f => f.status === "processing");
  useEffect(() => { if (!processing) return; const t = setInterval(load, 3000); return () => clearInterval(t); }, [processing, load]);

  const senden = async (file, name, extra = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", name);
    if (extra.source) fd.append("source", extra.source);
    if (extra.enhance) fd.append("enhance", "1");
    if (extra.prerollSec) fd.append("preroll_sec", String(extra.prerollSec));
    await api.post("/mbox/library", fd, { headers: { "Content-Type": "multipart/form-data" } });
  };
  const ERLAUBT = /\.(mp3|wav|m4a|ogg|aac|flac|opus|webm)$/i;
  const hochladenDateien = async (liste) => {
    const alle = Array.from(liste || []);
    const dateien = alle.filter(f => ERLAUBT.test(f.name) || (f.type || "").startsWith("audio/"));
    if (alle.length > dateien.length) toast.error(`${alle.length - dateien.length} Datei(en) übersprungen – nur Audiodateien (MP3, WAV, M4A, OGG, AAC, FLAC)`);
    if (!dateien.length) return;
    setBusy(true);
    let ok = 0;
    for (const file of dateien) {
      const basis = file.name.replace(/\.[^.]+$/, "");
      // eingegebener Name gilt nur bei einer einzelnen Datei, sonst Dateiname
      const name = dateien.length === 1 && uploadName.trim() ? uploadName.trim() : basis;
      try { await senden(file, name); ok += 1; }
      catch (err) { toast.error(`${file.name}: ${getErrorMsg(err, "Upload fehlgeschlagen")}`); }
    }
    if (ok) {
      toast.success(ok === 1 ? "Gespeichert – wird geprüft und auf 0 dB gebracht" : `${ok} Durchsagen gespeichert – werden geprüft und auf 0 dB gebracht`);
      setUploadName(""); setModus(null);
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
    load();
  };
  const hochladen = (e) => hochladenDateien(e.target.files);

  // Drag & Drop: Datei irgendwo auf den Bereich ziehen -> Upload-Feld oeffnet sich, Loslassen laedt hoch
  const hatDateien = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  const onDragEnter = (e) => {
    if (!hatDateien(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDrag(true);
    setModus(m => (m === "record" ? m : "upload"));
  };
  const onDragOver = (e) => { if (hatDateien(e)) { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; } };
  const onDragLeave = (e) => {
    if (!hatDateien(e)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDrag(false);
  };
  const onDrop = (e) => {
    if (!hatDateien(e)) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDrag(false);
    if (busy) return;
    hochladenDateien(e.dataTransfer.files);
  };
  const aufnahmeSpeichern = async (file, { name, prerollSec }) => {
    setBusy(true);
    try {
      await senden(file, name, { source: "record", enhance: true, prerollSec });
      toast.success("Aufnahme gespeichert – wird optimiert");
      setModus(null); load();
    } catch (err) { toast.error(getErrorMsg(err, "Speichern fehlgeschlagen")); }
    finally { setBusy(false); }
  };

  const files = data?.files || [];
  return (
    <section onDragEnter={fehler ? undefined : onDragEnter} onDragOver={fehler ? undefined : onDragOver} onDragLeave={fehler ? undefined : onDragLeave} onDrop={fehler ? undefined : onDrop}
      className={`bg-white border rounded-xl shadow-sm p-3 sm:p-5 space-y-4 transition ${drag ? "border-fuchsia-400 ring-2 ring-fuchsia-200" : "border-gray-200"}`} data-testid="customer-audio-library">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Library className="w-4 h-4 text-fuchsia-600" /> Meine Durchsagen-Datenbank</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Alle Durchsagen Ihrer Firma{data?.customer_no ? ` (Kundennummer ${data.customer_no})` : ""} – für alle Zugänge dieser Kundennummer sichtbar und mit jeder Veranstaltung verknüpfbar.
          </p>
        </div>
        {!fehler && (
          <div className="flex gap-2 w-full sm:w-auto">
            <Button size="sm" variant={modus === "upload" ? "default" : "outline"} onClick={() => setModus(m => m === "upload" ? null : "upload")} className={`flex-1 sm:flex-none h-10 sm:h-9 ${modus === "upload" ? "bg-fuchsia-600 hover:bg-fuchsia-700" : ""}`} data-testid="library-mode-upload">
              <Upload className="w-4 h-4 mr-1" /> Datei hochladen
            </Button>
            <Button size="sm" variant={modus === "record" ? "default" : "outline"} onClick={() => setModus(m => m === "record" ? null : "record")} className={`flex-1 sm:flex-none h-10 sm:h-9 ${modus === "record" ? "bg-red-600 hover:bg-red-700" : "text-red-700 border-red-200"}`} data-testid="library-mode-record">
              <Mic className="w-4 h-4 mr-1" /> Aufnehmen
            </Button>
          </div>
        )}
      </div>

      {fehler && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="library-error">{fehler}</p>}

      {modus === "upload" && (
        <div className={`rounded-lg border-2 border-dashed p-3 sm:p-4 space-y-3 transition ${drag ? "border-fuchsia-500 bg-fuchsia-50" : "border-gray-300 bg-gray-50"}`} data-testid="library-upload">
          <button type="button" disabled={busy} onClick={() => fileRef.current?.click()}
            className="w-full flex flex-col items-center justify-center gap-1 py-5 sm:py-6 rounded-md text-center hover:bg-white/60 disabled:opacity-60 transition" data-testid="library-dropzone">
            {busy ? <Loader2 className="w-7 h-7 text-fuchsia-600 animate-spin" /> : <Upload className={`w-7 h-7 ${drag ? "text-fuchsia-600" : "text-gray-400"}`} />}
            <span className="text-sm font-semibold text-gray-800">
              {busy ? "Wird hochgeladen…" : drag ? "Jetzt loslassen zum Hochladen" : (<><span className="hidden sm:inline">Audiodateien hierher ziehen oder </span><span className="text-fuchsia-700 underline">Datei auswählen</span></>)}
            </span>
            <span className="text-[11px] text-gray-400">MP3, WAV, M4A, OGG, AAC, FLAC · bis 25 MB je Datei · mehrere gleichzeitig möglich · wird auf Rauschen geprüft und auf 0 dB normalisiert</span>
          </button>
          <input ref={fileRef} type="file" multiple accept=".mp3,.wav,.m4a,.ogg,.aac,.flac,audio/*" onChange={hochladen} className="hidden" data-testid="library-upload-input" />
          <Input value={uploadName} onChange={e => setUploadName(e.target.value)} placeholder="Optional: Name der Durchsage (bei einer Datei), sonst Dateiname" className="h-10 sm:h-9 bg-white" data-testid="library-upload-name" />
        </div>
      )}
      {modus === "record" && (
        <div className="bg-gray-50 border border-dashed border-red-200 rounded-lg p-3" data-testid="library-record">
          <AudioRecorder busy={busy} onSave={aufnahmeSpeichern} defaultName="Meine Durchsage" maxSec={120} />
        </div>
      )}

      {!fehler && (data === null ? (
        <p className="text-sm text-gray-400">Lade Durchsagen…</p>
      ) : files.length === 0 ? (
        <p className="text-sm text-gray-400" data-testid="library-empty">Noch keine Durchsagen gespeichert. Laden Sie eine Datei hoch oder nehmen Sie direkt eine auf.</p>
      ) : (
        <ul data-testid="library-list">
          {files.map(f => <Eintrag key={f.id} f={f} orders={data.orders || []} onChange={(orderChanged) => { load(); if (orderChanged) onOrdersChanged?.(); }} />)}
        </ul>
      ))}
    </section>
  );
}
